import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { runDrag, type Held, type Press } from './dragSession';
import './dragSort.css';

/**
 * Pinterest's drag, with an iPhone's grouping on top of it: the thing you
 * grabbed lifts off the page and follows the cursor, everything else slides
 * around to open the hole it will drop into, and holding it still over
 * something instead puts it inside that thing.
 *
 * Pointer events rather than the browser's drag-and-drop. Native DnD hands
 * you a screenshot the browser draws and positions, `dragover` fires at its
 * own pace, and neither can be animated — the whole point here is the
 * animation. It's still no dependency, which is what the stage doc meant.
 *
 * React owns the order; `dragSession` owns the pixels.
 */

export type SortBinding = {
  'data-sort-id': string;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onClickCapture: (event: { preventDefault: () => void; stopPropagation: () => void }) => void;
};

export type DragSort = {
  /** What to render: the live arrangement mid-drag, the real one otherwise. */
  order: readonly string[];
  draggingId: string | undefined;
  /**
   * A sortable held over long enough to mean it. Releasing now groups rather
   * than reorders.
   */
  mergeId: string | undefined;
  /**
   * A declared drop zone — `data-drop-id` — held over long enough to mean it.
   * Releasing now sends the dragged thing there.
   */
  dropId: string | undefined;
  /**
   * The lifted element's size. A layout that can't reserve the slot itself —
   * a CSS grid, where a fixed element simply stops having a cell — needs this
   * to hold the space open.
   */
  size: { w: number; h: number } | undefined;
  /** Spread onto each sortable element. */
  bind: (id: string) => SortBinding;
};

export function useDragSort({
  ids,
  onCommit,
  onMerge,
  onSpring,
  enabled = true,
}: {
  ids: readonly string[];
  onCommit: (ordered: readonly string[]) => void;
  /** Held over another sortable and released. Without it, holding does nothing. */
  onMerge?: (draggedId: string, targetId: string) => void;
  /**
   * Held over a `data-drop-id` zone. Fires mid-drag — the drag carries on
   * afterwards, wherever that leaves it.
   */
  onSpring?: (draggedId: string, targetId: string) => void;
  enabled?: boolean;
}): DragSort {
  const [press, setPress] = useState<Press | undefined>(undefined);
  const [draggingId, setDraggingId] = useState<string | undefined>(undefined);
  const [held, setHeld] = useState<Held | undefined>(undefined);
  const [order, setOrder] = useState<readonly string[] | undefined>(undefined);

  // Refs, so a drag in progress isn't torn down and rebuilt every time the
  // callback identity changes or the live query hands back a fresh array.
  const commit = useRef(onCommit);
  commit.current = onCommit;

  const merge = useRef(onMerge);
  merge.current = onMerge;

  const spring = useRef(onSpring);
  spring.current = onSpring;

  const current = useRef(ids);
  current.current = ids;

  /** Set on release, read by the click that follows it. */
  const dragged = useRef(false);

  useEffect(() => {
    if (press === undefined) return;

    return runDrag({
      press,
      getIds: () => current.current,
      canMerge: merge.current !== undefined,
      onLift: () => {
        setDraggingId(press.id);
        setOrder(current.current);
      },
      onOrder: setOrder,
      onHolding: setHeld,
      onSpring: (targetId) => {
        // The arrangement this drag was building belonged to the container it
        // just left; whatever it lands in has its own.
        setOrder(undefined);
        spring.current?.(press.id, targetId);
      },
      onDone: (outcome) => {
        // The click that follows this release belongs to the drag, not to the
        // link underneath it.
        if (outcome.lifted) dragged.current = true;
        setDraggingId(undefined);
        setHeld(undefined);

        if (outcome.kind === 'merge') {
          setOrder(undefined);
          merge.current?.(press.id, outcome.targetId);
        } else if (outcome.kind === 'reorder' && !same(outcome.order, current.current)) {
          // Held, not cleared: the write below takes a moment to come back
          // through the database, and clearing now would flick every tile
          // back to where it started and then forward again.
          setOrder(outcome.order);
          commit.current(outcome.order);
        } else {
          setOrder(undefined);
        }

        setPress(undefined);
      },
    });
  }, [press]);

  // Once the database agrees, the held arrangement is just the arrangement.
  // A different length means something was added or deleted while it was
  // held, and that record wins over anything this was still showing.
  useEffect(() => {
    if (draggingId !== undefined || order === undefined) return;
    if (same(order, ids) || order.length !== ids.length) setOrder(undefined);
  }, [ids, order, draggingId]);

  return {
    order: order ?? ids,
    draggingId,
    mergeId: held?.kind === 'sort' ? held.id : undefined,
    dropId: held?.kind === 'drop' ? held.id : undefined,
    size: draggingId === undefined || press === undefined ? undefined : { w: press.w, h: press.h },
    bind: (id: string) => ({
      'data-sort-id': id,
      onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
        if (!enabled || event.button !== 0) return;
        // A finger is not a cursor. The same downward movement that scrolls a
        // room is the movement that starts a drag, and there is no way to
        // tell them apart at the moment the press lands — so scrolling a
        // board kept lifting tiles out of it.
        //
        // Tested on `pointerType` rather than on the device, so this is about
        // the input actually being used: a trackpad or a mouse on an iPad
        // still reorders, and nothing about the desktop changes.
        //
        // Reordering, grouping by holding one item over another, and dragging
        // into a section all go with it on touch. They are desk work, and the
        // phone is for looking. If they are ever wanted back, the way in is a
        // long press to arm the drag, not this line coming out.
        if (event.pointerType === 'touch') return;
        // Shift and Cmd/Ctrl are the multi-select gesture. Lifting the tile
        // out from under it would swallow the click that does the selecting.
        if (event.shiftKey || event.metaKey || event.ctrlKey) return;
        // A press on a control inside the card belongs to that control.
        if (event.target instanceof Element && event.target.closest('button, input, textarea')) {
          return;
        }

        dragged.current = false;
        const node = event.currentTarget;
        const rect = node.getBoundingClientRect();
        setPress({
          id,
          node,
          x: event.clientX,
          y: event.clientY,
          grabX: event.clientX - rect.left,
          grabY: event.clientY - rect.top,
          w: rect.width,
          h: rect.height,
        });
      },
      onClickCapture: (event: { preventDefault: () => void; stopPropagation: () => void }) => {
        if (!dragged.current) return;
        // Both: `preventDefault` is what react-router's Link checks, and
        // `stopPropagation` is what stops anything else listening.
        event.preventDefault();
        event.stopPropagation();
        dragged.current = false;
      },
    }),
  };
}

function same(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
